import { diffReplace, getCodeEditor } from "./codeEditorView";

/**
 * Живая синхронизация текстовых полей виджетов («Код», «SQL»).
 *
 * Своё: набранное уходит на сервер по короткой паузе в наборе и при уходе
 * из поля. Чужое: подставляется прямо в открытый редактор, даже если в нём
 * стоит курсор, — меняется только отличающийся кусок, курсор не прыгает.
 *
 * Единственное исключение: в поле есть набранное, но ещё не отправленное.
 * Тогда чужой текст не подставляем, иначе стёрли бы свои последние буквы;
 * через паузу наше уйдёт на сервер, и все увидят его.
 */

/** Пауза в наборе перед отправкой. Меньше — нагрузка на базу, больше — заметная задержка. */
export const LIVE_TEXT_SYNC_MS = 150;

type Entry = { lastSent: string; timer: number };

const entries = new WeakMap<Element, Entry>();

export type LiveTextSender = {
  /** Вызывать на каждый ввод: отправит после паузы. */
  schedule: () => void;
  /** Отправить сразу (уход из поля, запуск кода). */
  flush: () => void;
};

export function createLiveTextSender(
  field: Element,
  opts: {
    initial: string;
    enabled: boolean;
    read: () => string;
    send: (value: string) => void;
    delayMs?: number;
  }
): LiveTextSender {
  const entry: Entry = { lastSent: opts.initial, timer: 0 };
  entries.set(field, entry);
  const flush = () => {
    if (entry.timer) {
      window.clearTimeout(entry.timer);
      entry.timer = 0;
    }
    if (!opts.enabled) return;
    const v = opts.read();
    if (v === entry.lastSent) return;
    entry.lastSent = v;
    opts.send(v);
  };
  return {
    schedule: () => {
      if (!opts.enabled) return;
      if (entry.timer) window.clearTimeout(entry.timer);
      entry.timer = window.setTimeout(flush, opts.delayMs ?? LIVE_TEXT_SYNC_MS);
    },
    flush,
  };
}

/** Можно ли подставить чужой текст: нет своего неотправленного. */
function acceptsRemote(field: Element, current: string): boolean {
  const entry = entries.get(field);
  return !entry || current === entry.lastSent;
}

function markSynced(field: Element, value: string): void {
  const entry = entries.get(field);
  if (entry) entry.lastSent = value;
}

/** Чужой текст → редактор CodeMirror внутри `host`. */
export function applyRemoteToEditor(host: HTMLElement, remote: string): void {
  const ed = getCodeEditor(host);
  if (!ed) return;
  const current = ed.getValue();
  if (current === remote) {
    markSynced(host, remote);
    return;
  }
  if (!acceptsRemote(host, current)) return;
  ed.applyRemote(remote);
  markSynced(host, remote);
}

/** Чужой текст → обычное поле ввода; курсор сдвигается вместе с текстом. */
export function applyRemoteToTextarea(
  ta: HTMLTextAreaElement | HTMLInputElement,
  remote: string
): void {
  const current = ta.value;
  if (current === remote) {
    markSynced(ta, remote);
    return;
  }
  if (!acceptsRemote(ta, current)) return;
  const focused = ta.ownerDocument.activeElement === ta;
  const selStart = ta.selectionStart ?? 0;
  const selEnd = ta.selectionEnd ?? 0;
  const change = diffReplace(current, remote);
  ta.value = remote;
  if (focused && change) {
    const shift = (pos: number) => {
      if (pos <= change.from) return pos;
      if (pos >= change.to) return pos + change.insert.length - (change.to - change.from);
      return change.from + change.insert.length;
    };
    ta.setSelectionRange(shift(selStart), shift(selEnd));
  }
  markSynced(ta, remote);
}
